import React, { useState, useEffect } from "react";
import carrierService from "../../services/carrier.service";
import CarrierSelection from "./CarrierSelection";
import { useAgency } from "../../hooks/useAgency";
import * as Yup from "yup";
import _ from "lodash";

import { Alert, Button, Typography } from "@mui/material";

const Carrier = ({ completeStep, steps, user, data }) => {
  // restore carrier data if it exists
  const initialCarrierData = data?.contracts || [];
  const [carriers, setCarriers] = useState(initialCarrierData);
  const [carrierList, setCarrierList] = useState();
  const [loading, setLoading] = useState(false);
  // #34 — validation feedback. `attempted` flips on the first Next click; after
  // that we re-validate live so errors clear as the agent fixes each field.
  // `formErrors` is the summary list (names every missed field); `fieldErrors`
  // maps carrier row index -> { field: message } for inline highlighting.
  const [attempted, setAttempted] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const { agency } = useAgency();

  // #20 — the Individual/Corp choice only applies when the agent is paid as a
  // corporation/LLC (chosen on the Payment step). Otherwise the column is
  // hidden and policyEntity is left unset.
  const isCorporation = data?.payment === "corporation";

  const requiresState = (carrierId, carrierList) => {
    const carrierData = carrierList.find((c) => c.id === carrierId);
    return carrierData.states?.length > 0;
  };

  const validationSchema = Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required("Carrier is required"),
        states: Yup.array()
          .of(Yup.string())
          .when("value", {
            is: (value) => requiresState(value, carrierList),
            then: (schema) =>
              schema.min(1, "At least one state must be selected"),
            otherwise: (schema) => schema.optional(),
          }),
        policyEntity: isCorporation
          ? Yup.string().required("Individual/Corp is required")
          : Yup.string().optional(),
        policyCreation: Yup.string().required("New/Transfer is required"),
        withRelease: Yup.boolean().when("policyCreation", {
          is: (value) => value === "transfer",
          then: (schema) =>
            schema.required("Answer the release letter question for each transfer"),
          otherwise: (schema) => schema.optional(),
        }),
      })
    )
    .min(1, "Add at least one carrier");

  // #34 — run the schema and split the failures into a summary list (unique,
  // ordered messages that name each missed field) and a per-row/field map for
  // inline highlighting. Empty arrays mean the step is valid.
  const collectErrors = (list) => {
    try {
      validationSchema.validateSync(list, { abortEarly: false });
      return { formErrors: [], fieldErrors: {} };
    } catch (err) {
      const formErrors = [];
      const fields = {};
      (err.inner || []).forEach((e) => {
        if (!formErrors.includes(e.message)) formErrors.push(e.message);
        const m = /^\[(\d+)\]\.(\w+)/.exec(e.path || "");
        if (m) {
          const [, index, field] = m;
          fields[index] = fields[index] || {};
          fields[index][field] = fields[index][field] || e.message;
        }
      });
      // Array-level failures (e.g. no carriers at all) carry no `.inner`.
      if (!formErrors.length && err.message) formErrors.push(err.message);
      return { formErrors, fieldErrors: fields };
    }
  };

  // Get carrier list (only happens on load)
  useEffect(() => {
    carrierService.list(user).then((data) => {
      const carrierData = data.map((c) => {
        return {
          ...c,
          requiresState: c.states?.length > 0,
        };
      });
      console.log("carrierData", carrierData);
      setCarrierList(carrierData);
    });
  }, [agency, user]);

  // #34 — once the agent has attempted Next, keep the errors in sync with their
  // edits so each message disappears the moment its field is filled in.
  useEffect(() => {
    if (!attempted || !carrierList) return;
    const { formErrors, fieldErrors } = collectErrors(carriers);
    setFormErrors(formErrors);
    setFieldErrors(fieldErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriers, attempted, carrierList]);

  // Update a carrier option when a control changes.
  const updateOptions = (option, carrier, event) => {
    const value = event.target.value;
    setCarriers(
      carriers.map((c) => {
        if (c.value === carrier.value) {
          c[option] = value;
          // Clear the release answer when the carrier is no longer a Transfer, so
          // a stale "Yes" can't linger and trigger the Carrier Release step.
          if (option === "policyCreation" && value !== "transfer") {
            c.withRelease = undefined;
          }
        }
        return c;
      })
    );
  };

  function decorateCarrier(c) {
    const targetCarrier = _.find(carrierList, { id: c.value });
    c.label = targetCarrier.value;
    return c;
  }

  const submit = () => {
    // #34 — validate before advancing; if anything is missing, show which
    // field(s) rather than silently doing nothing.
    setAttempted(true);
    const errors = collectErrors(carriers);
    setFormErrors(errors.formErrors);
    setFieldErrors(errors.fieldErrors);
    if (errors.formErrors.length) return;

    let data = {};
    setLoading(true);

    data = {
      contracts: carriers.map((c) => {
        const decorated = decorateCarrier(c);
        // #20 — when not paid as a corporation/LLC, omit policyEntity entirely
        // (sending "" would fail the schema enum; undefined is dropped on send).
        if (!isCorporation) decorated.policyEntity = undefined;
        return decorated;
      }),
      agency: user?.agency,
    };
    completeStep(data);
  };

  if (!carrierList) return <></>;

  return (
    <>
      {/* #9/#10 — the list is filtered to the agent's licensed states. They set
          those once and can't change them here, so point them to Applied GA. */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        This list is based on the states you're licensed in. If a carrier you
        expect is missing, contact Applied GA to update your licensed states.
      </Typography>

      <CarrierSelection
        carrierList={carrierList}
        updateOptions={updateOptions}
        carriers={carriers}
        setCarriers={setCarriers}
        showPolicyEntity={isCorporation}
        fieldErrors={fieldErrors}
      />

      {formErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please complete the following before continuing:
          <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
            {formErrors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </Alert>
      )}
      <Button
        disabled={loading}
        fullWidth
        variant="contained"
        onClick={submit}
      >
        Next
      </Button>
    </>
  );
};

export default Carrier;
